// FedBD platform version metadata — frontend.
// CANONICAL source for the version pill and for re-acceptance triggering.
// Keep in sync with backend/app/__version__.py.
//
// Versioning policy: System Docs → Engineering → Versioning Policy.

// Platform SemVer — bump on every customer-visible change.
// MAJOR: breaking change to URL / auth / billing / data ownership / API
// MINOR: new user-visible feature
// PATCH: bug fix, copy edit, internal refactor
// v3.18.30 — SAM.gov API call shape compliance + key-expiry awareness.
//   Reported by super admin: created a SAM.gov API key but scans were
//   silently failing. Root cause: our /v2/search call was missing the
//   spec-required postedTo + offset parameters; lookback window was
//   only 24h (so even working calls returned empty most days); and
//   SAM.gov 90-day key auto-expiry had no UI surfacing.
//   - Backend (sentinel.py): _fetch_live_opportunities + test_connection
//     now send api_key + limit + offset + postedFrom + postedTo. Maps
//     401/403/404/429/502 to actionable messages. Lookback widened to
//     SAM_LOOKBACK_DAYS (default 30, env-configurable).
//   - Backend stores sam_gov_last_scan_result {checked_window, new,
//     total, fetched, error} on the org doc.
//   - GET /integrations/sam-gov now returns key_expires_at,
//     days_until_expiry, key_expiry_warning, last_scan_result.
//   - Frontend banner: amber warning when key ≤ 14 days from 90-day
//     auto-expiry; red error banner when last scan returned 401/403/etc.
//   - Frontend post-scan toast: "Scan complete — checked MM/DD–MM/DD,
//     fetched X of Y, Z new matches" or upstream error detail.

// v3.18.29 — Marketplace Sentinel header polish.
//   - Removed the "DEMO" badge from the SAM.gov tab header (was rendering
//     even when matches exist — visual clutter).
//   - Moved the eBuy "Sync eBuy folder" button into the SAME header slot
//     as SAM.gov's "Run Scan Now" (top-right of the page header). Both
//     tabs now have identical button position + identical "Last scan" /
//     "Last sync" pill placement.
//   - Removed the duplicate Sync card inside EbuyTab's left panel; the
//     panel now opens directly with Search + Filters.
//   - EbuyTab refactored with forwardRef + useImperativeHandle so the
//     parent header can call triggerSync(). Syncing state + latestSync
//     bubble up via onSyncStateChange / onLatestSyncChange callbacks.
// v3.18.31 — SAM/eBuy hardening pass on top of v3.18.30 scan-shape fix.
//   - sentinel.py: SentinelMatchingEngine.calculate_match now accepts
//     set_aside_qualifications from org settings; +10 score bump on a
//     qualifying setAside, set_aside_disqualified flag when the firm has
//     declared set-asides but the opp isn't one they qualify for. Match
//     metadata (set_aside_code/description/match/disqualified) persisted
//     onto sentinel_opportunities so the UI can dim/highlight downstream.
//   - sentinel.py: paginated /v2/search — offset += SAM_PAGE_SIZE (1000)
//     up to SAM_MAX_RECORDS_PER_SCAN (2000) per manual scan, stop-loss
//     re-checked before each page so a burst doesn't blow the 1000/day
//     hard cap. pages_fetched added to last_scan_result for audit.
//   - ebuy_sentinel.py: explicit 413 multipart-size guard (90 MB default,
//     EBUY_INGEST_MAX_BYTES env-tunable). Replaces the opaque ingress
//     error / "[object Object]" toast that surfaced on large attachment
//     folders.
//   - apiErrors.js: describeApiError now flattens Pydantic 422 arrays of
//     {loc, msg, type} into a readable "field: msg · field: msg" string;
//     new readFetchError(res) helper. EbuyTab callers (sync, claim,
//     dismiss) wired to it — toasts no longer say "[object Object]".
// v3.18.32 — SAM scan driven by response-deadline + per-NAICS ncode.
//   Bug report: sam.gov UI search "Response Due next 3 months · NAICS
//   541611/541612 · DHS/DOJ/HHS/DOT" returned 9 records but a FedBD
//   Sentinel scan returned zero. Root cause: our /v2/search call only
//   sent postedFrom/postedTo (30-day posted window) and filtered NAICS
//   client-side AFTER fetching, so opps posted >30 days ago with open
//   deadlines were invisible and the 100-row cap drowned real matches
//   in 30-day all-of-SAM noise.
//   - sentinel.py: search_opportunities + _fetch_live_opportunities
//     now accept rdl_from/rdl_to/ncode/ptype and send them to SAM.
//   - run_sentinel_scan loops once per target NAICS via ncode (server-
//     side pre-filter); defaults: rdl_from=today, rdl_to=today+90d,
//     postedFrom=today-365d, ptype="o,k,p,r" (Solicitation + Combined
//     Synopsis + Presolicitation + Sources Sought). Notice IDs deduped
//     across NAICS to avoid double-insert.
//   - checked_window now exposes rdl_from/rdl_to/ncode/ptype and the
//     scan's ncode_loop_count so the UI can show "checked rdl
//     MM/DD→MM/DD · 2 NAICS · X fetched / Y new" on next refresh.
// v3.19.0 — Documents-First flow for Client Profile KB cards.
//   User report: "PP section should just be a file upload — too painful
//   to enter the metadata for each one. The files already have all the
//   metadata."
//   Hybrid stub+async-extract pattern applied to all 4 card types:
//   Past Performance, Certifications, Key Personnel, Winning Proposals.
//   - New backend route POST /api/knowledge-base/{entity_type}/stub-from-files
//     accepts up to 10 multipart files (PDF/DOCX/TXT, 50 MB each).
//     For each file: creates a stub record (extraction_status="pending"),
//     attaches the file via the existing kb_attachments pipeline, and
//     enqueues a FastAPI BackgroundTask that runs AI extraction.
//   - Background extractor (kb_documents_first.py): pulls bytes from
//     GridFS, OCRs text (PyPDF2 / python-docx / utf-8 fallback), calls
//     BYOM-aware call_llm(purpose="kb_extract") with an entity-specific
//     JSON-output prompt, PATCHes the record with extracted metadata
//     (status flips to "completed" or "failed").
//   - Billing flows to the CLIENT org wallet via resolve_billing_org
//     so the consultant's wallet is never charged.
//   - New shared frontend components: KbDocumentsDropzone (replaces +Add
//     button; drag-drop + multi-file picker), ExtractionStatusBadge
//     (amber "Extracting…" while pending/running; red "Edit manually"
//     on failure; nothing when completed). ClientProfile +
//     WinningProposalsCard poll every 5s (max 30 polls ~2.5min) while
//     any row is still extracting. "Manual" ghost button retained for
//     power users who want to type metadata directly.
// v3.19.1 — Critical bug fix: Super Admin password reset.
//   User report: "Resetting password for any user never works — login
//   fails with 'Invalid email or password' immediately afterwards."
//   Root cause: mongo_admin.reset_user_password called bcrypt.hashpw()
//   directly, writing a $2b$... hash. But the rest of the codebase uses
//   a custom SHA256+salt format ("salt$hex") and verify_password()
//   tried to split('$') on the bcrypt prefix → False → login rejected.
//   Affected EVERY admin-reset user since the endpoint shipped.
//   - Backend: mongo_admin now uses the canonical hash_password()
//     helper (consistent across seed + signup + reset). Adds audit
//     log entry (ADMIN_PASSWORD_RESET).
//   - Backend: verify_password() accepts BOTH SHA256+salt and bcrypt
//     hashes for backward-compat — users already locked out can log
//     in with their last-known reset password once, and self-recover
//     on next reset.
//   - Frontend: "Forgot password?" link on LoginPage no longer points
//     to a nonexistent route. Now shows a clear next-step alert
//     directing the user to their Super Admin or support email (full
//     self-serve reset flow tracked on the roadmap).
//   - Tests: /app/backend/tests/test_v3191_password_reset.py (7/7).
// v3.20.0 — Self-serve forgot-password flow via SMTP.
//   Closes the loop on the v3.19.1 admin-only path. Users who forgot
//   their password can now reset themselves without admin involvement.
//   - Backend: POST /api/auth/forgot-password (returns same generic 200
//     regardless of email existence to prevent enumeration; rate-limited
//     3/hr/email via password_reset_attempts collection with TTL index;
//     single-use 32-char token persisted with 1h TTL in
//     password_reset_tokens). POST /api/auth/reset-password (explicit
//     400/410 errors — token invalid / expired / used; updates via
//     canonical hash_password()).
//   - Email: branded HTML via SMTP (Proton Mail STARTTLS on 587 by
//     default; provider-agnostic — any SMTP works). Sender:
//     contact@fedbd.ai.
//   - Frontend: /forgot-password (email input → success card) and
//     /reset-password?token=... (new password + confirm → redirect to
//     login). LoginPage "Forgot password?" link now points to the real
//     route (was a placeholder alert in v3.19.1).
//   - Tests: /app/backend/tests/test_v3200_self_serve_password_reset.py
// v3.20.1 — Code-quality pass on the actually-real findings from the
//   external code-review report. Most "Critical" findings were verified
//   false positives from a naive linter (eval() flag matched route path
//   "/eval"; "hardcoded secrets" were US-security-clearance enum values
//   like "SECRET"/"TOP_SECRET"; "missing hook deps" already pass the
//   project's ESLint config). Three real items fixed:
//   A. Empty catch blocks now log: StrategyWorkspace.jsx HUD-load,
//      OpportunityIntakeLive.jsx pursuit-evaluation fetch + outer
//      attemptFetch handler. No silent swallows.
//   B. Array-index-as-key replaced with stable IDs:
//      IngestionHub.jsx (partners + search results), OppIntakeLive.jsx
//      (key_vehicles), PastPerformanceDetail.jsx (technologies),
//      CapabilityStatement.jsx (match_reasons). Prevents lost form state
//      / animation glitches when lists reorder.
//   C. React-hook deps already clean — entire codebase passes ESLint
//      including react-hooks/exhaustive-deps. No changes needed.
//   Deferred (P2 sprint): localStorage→HttpOnly cookies migration,
//   large-component decomposition (RequirementsTable, ComplianceMatrix,
//   etc.), seed_super_admin breakdown, multi-param dataclass refactor.
//   These are real tech debt but out-of-scope for a patch release.
// v3.20.2 — Critical fix: eBuy sync 422 "archives: Input should be a
//   valid list". Pydantic 2.12 (shipped via the Feb 2026 dependency
//   refresh) tightened multipart validation and rejects
//   `Optional[List[UploadFile]] = File(None)` when the client sends 1+
//   files, treating each UploadFile dict as if it should already be a
//   list. The supported pattern is `List[UploadFile] = File([])` —
//   empty-list default, no Optional wrapper. Treats 0 archives the same
//   as before downstream. Sync with N>=1 attachments now succeeds again.
export const APP_VERSION = '2026.8.01';

// Legal-doc version. Bumping this triggers the re-acceptance modal on the
// next login for every signed-in user whose stored policy_version differs.
// Terms and Privacy share a single number for simpler acceptance tracking.
export const LATEST_POLICY_VERSION = '3.0';

// TEMPORARY HIDE SWITCH.
// When true:
//   - /terms and /privacy routes redirect to '/'
//   - footer + landing-page + signup links are hidden
//   - PolicyAcceptanceModal is suppressed (re-acceptance NOT enforced)
// Flip to `false` to restore the pages + re-acceptance flow without any
// other code changes. LATEST_POLICY_VERSION is preserved so enforcement
// resumes exactly where it left off.
//
// v3.11.10 — Pages re-enabled; LATEST_POLICY_VERSION = '3.0' will trigger
// the re-acceptance modal for every signed-in user on their next login.
export const LEGAL_PAGES_HIDDEN = false;
